/**
 * Vue Router Configuration
 * Routes for the SeeByEar Workshop App
 *
 * 路由配置
 * SeeByEar工作坊应用的路由
 */

import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type NavigationGuardNext,
} from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { watch } from 'vue'

// Route definitions
// 路由定义
const routes = [
  // Public routes / 公开路由
  {
    path: '/',
    name: 'home',
    component: () => import('@/pages/HomePage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/auth',
    name: 'auth',
    component: () => import('@/pages/AuthPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/pages/AuthCallbackPage.vue'),
    meta: { requiresAuth: false },
  },

  // Protected routes / 受保护路由
  {
    path: '/join/:code',
    name: 'join',
    component: () => import('@/pages/JoinPage.vue'),
    meta: { requiresAuth: true },
  },
  // Approval pending page / 审批等待页
  {
    path: '/approval-pending/:code',
    name: 'approval-pending',
    component: () => import('@/pages/ApprovalPendingPage.vue'),
    meta: { requiresAuth: true },
  },
  // Phase 1: Self Introduction / 阶段1：自我介绍
  {
    path: '/phase1/:code',
    name: 'phase1-intro',
    component: () => import('@/pages/Phase1LobbyPage.vue'),
    meta: { requiresAuth: true, requiresParticipant: true },
  },
  {
    path: '/team-reveal/:code',
    name: 'team-reveal',
    component: () => import('@/pages/TeamRevealPage.vue'),
    meta: { requiresAuth: true, requiresParticipant: true },
  },
  {
    path: '/phase2/:code',
    name: 'phase2-intro',
    component: () => import('@/pages/Phase2IntroPage.vue'),
    meta: { requiresAuth: true, requiresParticipant: true },
  },
  {
    path: '/phase3/:code',
    name: 'phase3-examples',
    component: () => import('@/pages/Phase3ExamplesPage.vue'),
    meta: { requiresAuth: true, requiresParticipant: true },
  },
  {
    path: '/phase4/:code',
    name: 'phase4-guide',
    component: () => import('@/pages/Phase4GuidePage.vue'),
    meta: { requiresAuth: true, requiresParticipant: true },
  },
  {
    path: '/phase5/:code',
    name: 'phase5-practice',
    component: () => import('@/pages/Phase5PracticePage.vue'),
    meta: { requiresAuth: true, requiresParticipant: true, requiresTeam: true },
  },
  {
    path: '/phase6/:code',
    name: 'phase6-creation',
    component: () => import('@/pages/Phase6CreationPage.vue'),
    meta: { requiresAuth: true, requiresParticipant: true, requiresTeam: true },
  },

  // Phase 7: Summary / 阶段7：总结
  {
    path: '/phase7/:code',
    name: 'phase7-summary',
    component: () => import('@/pages/Phase7SummaryPage.vue'),
    meta: { requiresAuth: true, requiresParticipant: true },
  },

  // Public summary (legacy) / 公开的总结页（旧路由）
  {
    path: '/summary/:code',
    name: 'summary',
    component: () => import('@/pages/Phase7SummaryPage.vue'),
    meta: { requiresAuth: false },
  },

  // Host routes / 主持人路由
  {
    path: '/host/login',
    name: 'host-login',
    component: () => import('@/pages/host/HostLoginPage.vue'),
    meta: { requiresAuth: false },
  },
  // Workshop creation routes / 工作坊创建路由
  {
    path: '/host/create',
    name: 'host-create',
    component: () => import('@/pages/host/CreateWorkshopPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/host/workshop/:id/edit',
    name: 'workshop-edit',
    component: () => import('@/pages/host/CreateWorkshopPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/host/:code',
    name: 'host-dashboard',
    component: () => import('@/pages/host/HostDashboard.vue'),
    meta: { requiresAuth: true, requiresHost: true },
  },
  {
    path: '/host/:code/screen',
    name: 'big-screen',
    component: () => import('@/pages/host/BigScreenPage.vue'),
    meta: { requiresAuth: false }, // Public for projector
  },
  {
    path: '/host/:code/materials',
    name: 'host-materials',
    component: () => import('@/pages/host/MaterialsPage.vue'),
    meta: { requiresAuth: true, requiresHost: true },
  },

  // 404 / 未找到页面
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
  },
]

// Create router instance
// 创建路由实例
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

/**
 * Authentication guard
 * Checks if user is authenticated before accessing protected routes
 *
 * 认证守卫
 * 在访问受保护路由前检查用户是否已认证
 */
async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore()

  // Wait for auth initialization
  // 等待认证初始化
  if (authStore.isLoading) {
    await new Promise<void>((resolve) => {
      const unwatch = watch(
        () => authStore.isLoading,
        (loading) => {
          if (!loading) {
            unwatch()
            resolve()
          }
        }
      )
    })
  }

  // Check if route requires authentication
  // 检查路由是否需要认证
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Store workshop code if joining via QR
    // 如果通过二维码加入，存储工作坊代码
    if (to.params.code) {
      authStore.setPendingWorkshopCode(to.params.code as string)
    }

    // Redirect to auth page
    // 重定向到认证页面
    return next({
      name: 'auth',
      query: { redirect: to.fullPath },
    })
  }

  next()
}

// Register navigation guards
// 注册导航守卫
router.beforeEach(authGuard)

export default router
